import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import LogoDark from '@images/logoDark.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import { useEffect, useState } from 'react';
import { LinkExpiredPage } from '@/components/LinkExpiredPage';
import {
  useConfirmForgotPassword,
  useOnboard,
  useOnboardPayload,
  useResetPasswordPayload,
} from '@/services/authService';
import { useUserStore } from '@/stores/user';
import Loader from '@/components/Loader';
import { ChangePassword, errorList } from '@/components/ChangePassword';
import { displayError } from '@/lib/error';

const resetFormSchema = z
  .object({
    newPassword: z.string({ message: 'New password is required' }),
    retypeNewPassword: z.string({ message: 'New password is required' }),
  })
  .refine((data) => data.newPassword === data.retypeNewPassword, {
    message: 'Passwords do not match. Please try again.',
    path: ['retypeNewPassword'],
  });

export const ResetPassword = ({ type }: { type: 'FORGOT_PASSWORD' | 'RESET_PASSWORD' }) => {
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const { payload } = useParams();
  const userStore = useUserStore();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof resetFormSchema>>({
    resolver: zodResolver(resetFormSchema),
    defaultValues: {
      newPassword: '',
      retypeNewPassword: '',
    },
    mode: 'onTouched',
  });

  const {
    watch,
    formState: { isValid },
    getValues,
    handleSubmit,
  } = form;

  const { error: payloadError, isLoading: onboardingPayloadLoading } = useOnboardPayload(payload, {
    enabled: type === 'RESET_PASSWORD',
  });
  const { error: forgotPasswordPayloadError, isLoading: resetPayloadLoading } = useResetPasswordPayload(payload, {
    enabled: type === 'FORGOT_PASSWORD',
  });
  const { mutate: resetPassword, status } =
    type === 'RESET_PASSWORD' ? useOnboard() : useConfirmForgotPassword();

  useEffect(() => {
    if (forgotPasswordPayloadError ?? payloadError) setIsLinkExpired(true);
  }, [payloadError, forgotPasswordPayloadError]);

  const onSubmitHandler = () => {
    resetPassword(
      {
        data: {
          newPassword: getValues('newPassword'),
          payload: payload!,
        },
      },
      {
        onSuccess: (e) => {
          userStore.reset();
          navigate('/login');
          toast({
            description: e.message,
            className: '!relative !top-[-60px]',
            variant: 'default',
            duration: 5000,
          });
        },
        onError: (e) => {
          displayError(e, 5000, '!relative !top-[-60px]');
        },
      }
    );
  };

  const password = watch('newPassword');
  return (
    <div className="bg-white flex flex-col items-center justify-center h-screen min-h-[600px]">
      {isLinkExpired ? (
        <LinkExpiredPage />
      ) : (
        <form onSubmit={handleSubmit(onSubmitHandler)} className="min-w-[400px] max-w-[468px] min-h-[580px]">
          <Card className="flex bg-white rounded- shadow w-full min-w-[325px] max-w-[468px] min-h-[580px] p-10 gap-6 items-center">
            <Loader isLoading={onboardingPayloadLoading || resetPayloadLoading}>
              <CardContent className="flex flex-col gap-10 w-full p-0">
                <FormProvider {...form}>
                  <div className="flex flex-col gap-10 justify-start ">
                    <div className="w-full flex justify-center">
                      <img src={LogoDark} alt="logo" className="w-[135px] h-[70px]" />
                    </div>
                    <div className="flex gap-2 flex-col">
                      <span className="flex text-2xl font-semibold">
                        {type === 'RESET_PASSWORD' ? 'Set ' : 'Reset '}Password
                      </span>
                      <span className="text-sm font-400 flex">{`Enter your ${type === 'RESET_PASSWORD' ? 'new' : ''} password.`}</span>
                    </div>
                  </div>
                  <ChangePassword />
                  <div className="flex flex-col justify-start">
                    <Button
                      type="submit"
                      className="w-full text-white rounded-md focus:outline-none text-sm font-medium"
                      isLoading={status === 'pending'}
                      disabled={
                        status === 'pending' ||
                        !getValues('newPassword') ||
                        !getValues('retypeNewPassword') ||
                        !isValid ||
                        errorList.some((el) => !el.regex.test(password))
                      }
                    >
                      {type === 'RESET_PASSWORD' ? 'Set ' : 'Reset '}Password
                    </Button>
                  </div>
                </FormProvider>
              </CardContent>
            </Loader>
          </Card>
        </form>
      )}
    </div>
  );
};
